
import React from "react";
import { Link } from "react-router-dom";
import config from "../config";
import { PRACTICE, lateCancelFeeText, paymentTimingText } from "./practice";

/*
 * Marketing copy for each service, keyed by slug.
 *
 * The backend (GET /api/public/services) is authoritative for price,
 * duration and bookable_online; the values below are the seeded launch
 * values (owner's November 2026 price list) and are only a fallback for
 * when the API is unreachable. Keep them equal to the backend seed.
 *
 * The free 15-minute consultation is gone (her November 2026 corrections):
 * people request a call instead (components/RequestCallForm.jsx).
 */

const { cancellationWindowHours: CANCEL_HOURS, meetingPlatform } = PRACTICE;
const contactEmail = config.business.contactEmail;
const INTAKE_PRICE = 195;

// Her round-2 answers 1.1 and 1.2: pay by transfer or cheque before or at
// the session; bank details by email on request.
function PaymentMethod() {
  return (
    <p>
      Payment is accepted via bank transfer or cheque payable to Jasmine Smith at First Caribbean, before or at your
      session. To receive bank transfer details, email <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
    </p>
  );
}

// TODO(owner): Wave B: her copy adds "All payments are non-refundable once
// processed"; left out until the refund/payment-timing decision is made.
function CancellationPolicy() {
  return (
    <>
      <h2>Cancellation Policy</h2>
      <p>
        Sessions must be canceled or rescheduled at least {CANCEL_HOURS} hours in advance to avoid a cancellation
        fee. Late cancellations and no-shows may incur a fee of up to {lateCancelFeeText()}, payable at your next
        session. Nothing is charged automatically. A one-time waiver is available for first-time no-shows. To cancel
        or reschedule, reply to your confirmation email. Read more in my{" "}
        <Link to="/policies#refund">Refund &amp; Cancellation Policy</Link> for full details.
      </p>
    </>
  );
}

// Online bookings are requests she confirms by email (round 2, answer 6);
// sessions use a Google Meet link from the website (answer 1.4). Her "prep
// tips by email" step is not promised.
function HowToSchedule() {
  return (
    <>
      <h2>How to schedule</h2>
      <ol>
        <li>Click <strong>Book Now</strong> and choose the next available date (times are shown in Bahamas time).</li>
        <li>
          Enter your details and send your booking request. {paymentTimingText()}
        </li>
        <li>
          Online bookings are requests: I confirm each one by email. Your {meetingPlatform} video link is sent by
          email before your session.
        </li>
      </ol>
    </>
  );
}

export const SERVICES = [
  {
    slug: "intake-session",
    title: "Intake Session",
    short: "Every therapeutic journey begins here.",
    price: INTAKE_PRICE,
    duration: "1 hr 15 min",
    durationMinutes: 75,
    bookableOnline: true,
    long: (
      <>
      <h2>Service Description</h2>
        <p>
          The intake session is our first time meeting: a dedicated space for you to share what's brought you to therapy, what you're hoping for, and what feels important for me to know. I'll listen, ask questions, and together we'll begin to map out what support looks like for you. This session is required before beginning individual therapy.
        </p>
        <p>
          The intake session is your first official step into therapy. During this session, we’ll explore your reasons for seeking support, your goals, and any relevant history. It’s a collaborative space designed to help us build a foundation for your care, whether you’re interested in talk therapy, art therapy, or both.
        </p>
        <p>
          This is a one-time session with a flat fee of ${INTAKE_PRICE}, separate from ongoing session costs. Sliding scale available upon request.
        </p>
        <PaymentMethod />
        <CancellationPolicy />
        <HowToSchedule />
      </>
    ),
  },
  {
    slug: "individual-talk-therapy",
    title: "Talk Therapy",
    short: "Ongoing therapy through honest conversation, a consistent space to process, reflect, and grow.",
    price: 170,
    duration: "50 min",
    durationMinutes: 50,
    bookableOnline: true,
    long: (
      <>
      <h2>Service Description</h2>
        <p>
          Sometimes you just need someone who is trained to listen and help you make sense of what you’re experiencing. Talk therapy offers space to explore your emotions, understand what they may be communicating, recognize patterns, and navigate life’s challenges with greater self-awareness and curiosity rather than judgment.
        </p>
        <PaymentMethod />
        <CancellationPolicy />
        <HowToSchedule />
      </>
    ),
  },
  {
    slug: "individual-art-therapy",
    title: "Art Therapy",
    short: "Your art doesn’t have to be pretty. It just has to be yours.",
    price: 170,
    duration: "50 min",
    durationMinutes: 50,
    bookableOnline: true,
    long: (
      <>
      <h2>Service Description</h2>
        <p>
          You don’t have to be an artist to benefit from art therapy. In these sessions, we use creative expression, such as drawing, collage, painting, and more, to explore experiences and process emotions in ways that words alone sometimes cannot. Art therapy can be especially helpful for people who feel stuck, overwhelmed, or unsure where to begin.
        </p>
        <PaymentMethod />
        <CancellationPolicy />
        <HowToSchedule />
      </>
    ),
  },
  {
    slug: "extended-talk-therapy",
    title: "Extended Talk Therapy",
    short: "Same warmth, same support, just a little more time.",
    price: 195,
    duration: "1 hr 25 min",
    durationMinutes: 85,
    bookableOnline: true,
    long: (
      <>
      <h2>Service Description</h2>
        <p>
          For those times when 50 minutes just isn’t enough. Extended sessions offer more space to go deeper, move slower, or work through something that needs more room.
        </p>
        <PaymentMethod />
        <CancellationPolicy />
        <HowToSchedule />
      </>
    ),
  },
  {
    slug: "extended-individual-art-therapy",
    title: "Extended Art Therapy",
    short: "For when the creative process needs more room to breathe.",
    price: 205,
    duration: "1 hr 25 min",
    durationMinutes: 85,
    bookableOnline: true,
    long: (
      <>
      <h2>Service Description</h2>
        <p>
          An extended art therapy session ideal for those who find that they’re just getting started when the hour is up. More time to create, reflect, and let the work unfold at your own pace.
        </p>
        <PaymentMethod />
        <CancellationPolicy />
        <HowToSchedule />
      </>
    ),
  },
  {
    slug: "safe-space-session",
    title: "Safe Space Session",
    short: "A little room to pause, breathe, and talk about what’s on your heart.",
    price: 60,
    duration: "45 min",
    durationMinutes: 45,
    bookableOnline: true,
    long: (
      <>
      <h2>Service Description</h2>
        <p>
          No long-term commitment is required. Safe Space sessions offer a supportive space to talk about what’s on your heart, whether you’re working through something specific or simply carrying a feeling you can’t quite shake. It’s an opportunity to slow down, exhale, and have someone listen without judgment.
        </p>
        <PaymentMethod />
        <CancellationPolicy />
        <HowToSchedule />
      </>
    ),
  },
];

/** 50 -> "50 min", 75 -> "1 hr 15 min", 60 -> "1 hr" (matches the copy above). */
export function formatDuration(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}
